/**
 * Shipment Overview widgets — the clickable status/filter counters above the list.
 *
 * Storage rides on the per-user saved-views API, the same way pinned view tabs do:
 * each widget is one saved-view record under a dedicated page key, so a user's
 * chosen widgets and their order survive logout and follow them across devices
 * with no backend change. The config blob only uses keys `buildViewConfig` already
 * names — `filter` for the quick status and `advancedFilters` for the rule-based
 * ones — because a backend that deserialises config into a typed DTO drops keys it
 * does not declare. Position rides in `order`, the same deviation view tabs make
 * for want of a field of its own.
 *
 * Until a user saves anything there are no records at all, and the strip falls
 * back to one widget per shipment status — so the feature works before it is ever
 * customised, and "reset to defaults" is simply deleting every record.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { tableSavedViewsApi, TABLE_PAGE_KEYS } from "../../../TableSavedViews";
// Ordering is identical to the view tabs' — position in a config blob, records
// predating it sorting last — so the tested sort is reused rather than copied.
import { sortViewTabs as sortWidgets } from "../ViewTabs/viewTabOrder";
import { useShipmentViewTabs } from "../ViewTabs";
import {
  DEFAULT_ADVANCED_FILTERS,
  countActiveFilters,
  describeNode,
  filtersOverrideStatus,
  isNodeComplete,
  ruleMatchType,
} from "../TabContent/shipmentShared";
import { toViewStatus } from "../TabContent/Utils/shipmentStatuses";

export const SHIPMENT_OVERVIEW_WIDGETS_PAGE_KEY =
  TABLE_PAGE_KEYS.SHIPMENT_OVERVIEW_WIDGETS;

/**
 * Longest widget name we will store. The saved-views record answers a name past
 * its own column length with a 400, which surfaces as a failed save the user can
 * do nothing about — so names are cut here (and in the inputs) instead.
 */
export const WIDGET_NAME_MAX_LENGTH = 100;

/** A name as it will be stored: trimmed, cut to length, never empty. */
export const normalizeWidgetName = (name) =>
  String(name ?? "").trim().slice(0, WIDGET_NAME_MAX_LENGTH) || "Untitled";

/** Content type of the tab a widget opens when it has none of its own. */
export const OVERVIEW_TAB_CONTENT_TYPE = "overviewWidget";

/** Content type of the user's own pinned tabs, as the tab bar files them. */
const SAVED_TAB_CONTENT_TYPE = "savedViewTab";

const isPlainConfig = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/** Same tolerance as the saved-views hook: `config` may come back as JSON text. */
const normalizeConfig = (config) => {
  if (typeof config === "string") {
    try {
      const parsed = JSON.parse(config);
      return isPlainConfig(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return isPlainConfig(config) ? config : {};
};

export const normalizeFilters = (filters) =>
  isPlainConfig(filters) ? { ...DEFAULT_ADVANCED_FILTERS, ...filters } : DEFAULT_ADVANCED_FILTERS;

/**
 * Identity of a widget across reloads and across the default/saved boundary, used
 * to match a widget to the tab it opened. A saved widget keeps its record id even
 * if it is renamed or re-filtered; a default one is identified by its status.
 */
export const widgetKey = (widget) =>
  widget?.id != null ? `id:${widget.id}` : `status:${widget?.status ?? "all"}`;

/**
 * Whether an open widget tab already lists what this widget counts.
 *
 * A tab records the status and filter it opened with, so this is what stops a
 * second, identical tab being opened for a widget whose key has moved on — the
 * first save turns a default widget into a record, taking its key from
 * `status:New` to `id:42` while the tab it opened still names the old one.
 */
export const tabOpensSameShipments = (tab, widget) =>
  (tab?.status ?? null) === (widget?.status ?? null) &&
  JSON.stringify(normalizeFilters(tab?.advancedFilters)) ===
    JSON.stringify(normalizeFilters(widget?.advancedFilters));

const sameName = (a, b) => {
  const left = String(a ?? "").trim().toLowerCase();
  return Boolean(left) && left === String(b ?? "").trim().toLowerCase();
};

/**
 * The tab a widget should open into, or -1 for one that needs opening.
 *
 * A widget's own tab comes first, then the user's pinned tab of the same name: a
 * widget added from "New + Costco" belongs to that tab, which is open from login
 * anyway, so opening a second tab beside it under the same name is just a
 * duplicate of what the user already had.
 */
export const findTabForWidget = (tabs = [], widget) => {
  const key = widgetKey(widget);

  const ownTab = tabs.findIndex(
    (tab) =>
      tab?.contentType === OVERVIEW_TAB_CONTENT_TYPE &&
      // Matching on what the tab counts as well as on the widget it came from:
      // the first save turns a default widget into a record, so its key changes
      // from `status:New` to `id:42` while the tab it opened still says the old
      // one. Without this that widget would open a second, identical tab.
      (tab.widgetKey === key || tabOpensSameShipments(tab, widget))
  );
  if (ownTab >= 0) return ownTab;

  return tabs.findIndex(
    (tab) =>
      tab?.contentType === SAVED_TAB_CONTENT_TYPE && sameName(tab.name, widget?.name)
  );
};

/**
 * The widget as revised by the filter editor.
 *
 * The editor opens with the widget's status already written in as a rule, and a
 * status rule makes the quick one redundant — `buildFilterNode` drops it anyway.
 * But the rule builder can't express every status the backend has (In Transit is
 * not one of its own), so where the rules say nothing about status the widget
 * keeps the one it had, rather than quietly widening to every shipment.
 */
export const withFilters = (widget, filters) => ({
  ...widget,
  advancedFilters: normalizeFilters(filters),
  status: filtersOverrideStatus(filters) ? null : widget?.status ?? null,
});

/** Does this widget narrow by anything beyond its status? */
export const hasAdvancedFilters = (widget) =>
  countActiveFilters(normalizeFilters(widget?.advancedFilters)) > 0;

/**
 * "Compare with" presets, in minutes — the only unit the backend needs, since a
 * rolling comparison window is just "count the last N minutes vs. the N minutes
 * before that." Custom values (any positive integer of minutes) are equally
 * valid; these are just the shortcuts offered in the widget editor.
 */
export const COMPARE_PRESETS = [
  { label: "No comparison", minutes: null },
  { label: "Yesterday", minutes: 1440 },
  { label: "Last week", minutes: 10080 },
  { label: "Last month", minutes: 43200 },
  { label: "Last quarter", minutes: 129600 },
];

/** The preset a stored minute count matches, or null for a custom value. */
export const comparePresetForMinutes = (minutes) =>
  COMPARE_PRESETS.find((p) => p.minutes === minutes) ?? null;

/**
 * How a widget's matching shipments are reduced to one number. COUNT is the original/default
 * behaviour; SUM/AVERAGE need a numeric field to reduce (AGGREGATION_FIELDS below) — a "Total
 * Charges" widget as a *count* of matching shipments doesn't mean anything, sum or average
 * does.
 */
export const AGGREGATION_OPTIONS = [
  { key: "COUNT", label: "Count" },
  { key: "SUM", label: "Sum" },
  { key: "AVERAGE", label: "Average" },
];

/**
 * Numeric fields SUM/AVERAGE can reduce. Deliberately narrow: `totalCharge`/`totalWeight` look
 * like obvious candidates but are stored as free-text on the shipment record (a pre-existing,
 * unrelated data quirk), not a number the backend can safely SUM/AVG. TOTAL_CHARGES instead
 * sums the shipment's actual (properly-typed) charge line items — also the more correct
 * reading of "total shipment charges" anyway.
 */
export const AGGREGATION_FIELDS = [
  { key: "TOTAL_CHARGES", label: "Total Charges ($)" },
  { key: "TOTAL_MILES", label: "Total Miles" },
];

/** "$12,345.67" / "1,204.5 mi" — how a widget's own aggregation formats its raw number. */
export const formatAggregateValue = (widget, rawValue) => {
  if (rawValue === null || rawValue === undefined) return null;
  const num = Number(rawValue);
  if (Number.isNaN(num)) return null;
  if (widget.aggregation !== "COUNT" && widget.aggregationField === "TOTAL_CHARGES") {
    return num.toLocaleString(undefined, { style: "currency", currency: "USD" });
  }
  const plain = num.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return widget.aggregation !== "COUNT" && widget.aggregationField === "TOTAL_MILES"
    ? `${plain} mi`
    : plain;
};

/**
 * What a Sum/Average card is measuring, for the line under its value — a card named
 * "New" showing "$717.75" gives no hint that it is an average. Empty for Count, where
 * the name already says what is counted.
 */
export const describeAggregation = (widget) => {
  if (!widget?.aggregation || widget.aggregation === "COUNT") return "";
  const field = AGGREGATION_FIELDS.find((f) => f.key === widget.aggregationField);
  const what = field?.key === "TOTAL_MILES" ? "total miles" : "total charges";
  return widget.aggregation === "AVERAGE" ? `Avg ${what} per shipment` : `Sum of ${what}`;
};

/**
 * Identity of everything a widget's number depends on. Values are stored under this
 * rather than under `widgetKey` alone: editing a widget keeps its key, so a value
 * fetched for the old settings would otherwise be shown — formatted for the new
 * ones — until the refetch lands (a Count of 4 flashing as "$4.00" after switching
 * to Sum of charges).
 */
export const widgetValueKey = (widget) =>
  JSON.stringify([
    widgetKey(widget),
    widget?.status ?? null,
    normalizeFilters(widget?.advancedFilters),
    widget?.aggregation || "COUNT",
    widget?.aggregationField ?? null,
    widget?.comparePeriodMinutes ?? null,
  ]);

/** "vs last week" / "vs the last 45 minutes" — the chip's comparison label. */
export const describeComparePeriod = (minutes) => {
  if (!minutes || minutes <= 0) return "";
  const preset = comparePresetForMinutes(minutes);
  if (preset) return `vs ${preset.label.toLowerCase()}`;
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return `vs the last ${days} day${days === 1 ? "" : "s"}`;
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `vs the last ${hours} hour${hours === 1 ? "" : "s"}`;
  }
  return `vs the last ${minutes} minute${minutes === 1 ? "" : "s"}`;
};

/** The widget's filter in words, for its tooltip and its row in the manager. */
export const describeWidgetFilters = (widget) => {
  const filters = normalizeFilters(widget?.advancedFilters);
  const nodes = (filters.rules || []).filter(isNodeComplete);
  const joiner = ruleMatchType(filters) === "OR" ? " or " : " and ";
  const rules = nodes.map(describeNode).filter(Boolean).join(joiner);
  const status = widget?.status ? `Status is ${toViewStatus(widget.status)}` : "";

  if (rules && status) return `${status} and ${rules}`;
  return rules || status || "All shipments";
};

const toWidget = (record) => {
  const config = normalizeConfig(record.config);
  return {
    id: record.id,
    name: record.name,
    // The quick status filter and the rule-based filters keep the names the table's
    // own saved views give them, so the blob stays inside the declared keys.
    status: config.filter ?? null,
    advancedFilters: normalizeFilters(config.advancedFilters),
    order: Number.isFinite(config.order) ? config.order : null,
    comparePeriodMinutes: Number.isFinite(config.comparePeriodMinutes)
      ? config.comparePeriodMinutes
      : null,
    aggregation: config.aggregation || "COUNT",
    aggregationField: config.aggregationField || null,
  };
};

const toConfig = (widget, order) => ({
  filter: widget.status ?? null,
  advancedFilters: normalizeFilters(widget.advancedFilters),
  order,
  comparePeriodMinutes: Number.isFinite(widget.comparePeriodMinutes)
    ? widget.comparePeriodMinutes
    : null,
  aggregation: widget.aggregation || "COUNT",
  aggregationField: widget.aggregation && widget.aggregation !== "COUNT"
    ? widget.aggregationField || null
    : null,
});

/**
 * The user's saved shipment tabs, as widgets they could add.
 *
 * A pinned tab already carries the two things a widget is made of — a quick status
 * in `filter` and a rule set in `advancedFilters` — so a tab the user has set up is
 * a counter they can have for free, and the widget lands on exactly the shipments
 * that tab opens on. Tabs that narrow nothing are left out: as widgets they would
 * all be the same "every shipment" count.
 */
export function useSavedTabSuggestions({ enabled = true } = {}) {
  // Reuses the tab bar's own loader rather than re-listing the page key here: it
  // already tolerates a config that comes back as JSON text, and a failure there
  // leaves an empty list, which is the right outcome for a convenience list.
  const { viewTabs } = useShipmentViewTabs({ enabled });

  return useMemo(
    () =>
      viewTabs
        .map((tab) => ({
          id: tab.id,
          name: tab.name,
          status: tab.config?.filter ?? null,
          advancedFilters: normalizeFilters(tab.config?.advancedFilters),
        }))
        .filter((tab) => tab.name && (tab.status || hasAdvancedFilters(tab))),
    [viewTabs]
  );
}

/** The strip a user who has never customised anything sees: one widget per status. */
export const defaultWidgets = (apiStatuses = []) =>
  apiStatuses.map((status, index) => ({
    id: null,
    name: toViewStatus(status),
    status,
    advancedFilters: DEFAULT_ADVANCED_FILTERS,
    order: index,
    comparePeriodMinutes: null,
    aggregation: "COUNT",
    aggregationField: null,
  }));

const configsMatch = (a, b) =>
  (a.status ?? null) === (b.status ?? null) &&
  (a.comparePeriodMinutes ?? null) === (b.comparePeriodMinutes ?? null) &&
  (a.aggregation || "COUNT") === (b.aggregation || "COUNT") &&
  (a.aggregationField ?? null) === (b.aggregationField ?? null) &&
  JSON.stringify(normalizeFilters(a.advancedFilters)) ===
    JSON.stringify(normalizeFilters(b.advancedFilters));

/**
 * The user's overview widgets, plus the writes the manager modal needs.
 *
 * `widgets` is what the strip renders: the saved records once there are any,
 * otherwise the per-status defaults derived from `apiStatuses`.
 */
export function useOverviewWidgets({ apiStatuses = [], enabled = true } = {}) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  // Reorder and save read the whole current list before writing, so they need it
  // outside of a setState updater.
  const recordsRef = useRef(records);
  recordsRef.current = records;

  const reload = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await tableSavedViewsApi.list(SHIPMENT_OVERVIEW_WIDGETS_PAGE_KEY);
      setRecords(sortWidgets((Array.isArray(data) ? data : []).map(toWidget)));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load widgets");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  const isCustomized = records.length > 0;

  const widgets = useMemo(
    () => (isCustomized ? records : defaultWidgets(apiStatuses)),
    [isCustomized, records, apiStatuses]
  );

  /**
   * Persist the strip the modal was left with, as a diff against what is stored:
   * widgets dropped from the list are deleted, new ones created, and the rest
   * updated only where their name, filter or position actually changed.
   *
   * Saving a list that is still the untouched defaults writes the whole set out —
   * that first save is what turns "no records, showing defaults" into a layout the
   * user owns and can reorder.
   */
  const saveWidgets = useCallback(
    async (nextWidgets = []) => {
      const stored = recordsRef.current;
      const keptIds = new Set(
        nextWidgets.filter((w) => w.id != null).map((w) => String(w.id))
      );

      const deletes = stored
        .filter((w) => !keptIds.has(String(w.id)))
        .map((w) => tableSavedViewsApi.remove(w.id));

      const writes = nextWidgets.map((widget, index) => {
        // Cut here rather than only in the inputs: a widget added from a pinned tab
        // takes that tab's name, which nothing has been holding to this length.
        const name = normalizeWidgetName(widget.name);
        const config = toConfig(widget, index);

        if (widget.id == null) {
          return tableSavedViewsApi.create({
            pageKey: SHIPMENT_OVERVIEW_WIDGETS_PAGE_KEY,
            name,
            isDefault: false,
            config,
          });
        }

        const existing = stored.find((w) => String(w.id) === String(widget.id));
        const unchanged =
          existing &&
          existing.name === name &&
          existing.order === index &&
          configsMatch(existing, widget);

        return unchanged ? null : tableSavedViewsApi.update(widget.id, { name, config });
      });

      await Promise.all([...deletes, ...writes.filter(Boolean)]);
      // Re-list rather than patching locally: creates need their new ids before the
      // strip can dedupe tabs or the modal can edit the rows again.
      await reload();
    },
    [reload]
  );

  /** Drop every record, so the strip falls back to one widget per status. */
  const resetWidgets = useCallback(async () => {
    await Promise.all(recordsRef.current.map((w) => tableSavedViewsApi.remove(w.id)));
    await reload();
  }, [reload]);

  return useMemo(
    () => ({
      widgets,
      isCustomized,
      loading,
      error,
      reload,
      saveWidgets,
      resetWidgets,
    }),
    [widgets, isCustomized, loading, error, reload, saveWidgets, resetWidgets]
  );
}
